import setWith from 'lodash/set';

const isObject = x => Object(x) === x;

const mutate = (obj, [k, v]) => {
  const newObj = { ...obj };
  newObj[k] = v;

  return newObj;
};

const merge = (left = {}, right = {}) => {
  return Object.entries(right).map(([k, v]) => (isObject(v) && isObject(left[k]) ? [k, merge(left[k], v)] : [k, v])).reduce(mutate, left);
};


export const objectDiff = (l = {}, r = {}) => {
  const changes = [];
  const diffObject = {};

  // Main differential function
  const innerObjDiff = (left = {}, right = {}, path = []) => {    
    Object.entries(left).forEach(([k, v]) => { // Loop through all keys in the first object and check each for differences      
      let isDiff = false;

      if (isObject(v) && isObject(right[k])) { // If value is an object
        if (Array.isArray(v) && Array.isArray(right[k])) { // If value object is specifically an array
          if (v.length !== right[k].length) { // If array lengths don't match, there are differences and push entire array as changed
          changes.push({ key: k, value: right[k], path });
          } else { // Check if any array items are different
            v.forEach((arrayValue, index) => {
              if (!isDiff) {
                if (isObject(arrayValue) && isObject(right[k][index])) { // If item is object, start a nestedOBjectDiff process where it can break out when a single difference is found
                  isDiff = nestedObjectDiff(arrayValue, right[k][index]);
                  if (isDiff) { changes.push({ key: k, value: right[k], path }); }
                } else if (!isDiff && right[k][index] !== arrayValue) { // If the item is just a value make a comparison, if the difference exists add array to changed array
                  isDiff = true;
                  changes.push({ key: k, value: right[k], path });
                }
              }
            });
          }
        } else {
          const leftObjectKeyNames = Object.entries(v).map(filterKey => filterKey[0]);
          const rightObjectKeys = Object.entries(right[k]);
  
          // If number of keys of right is more than the left, there are differences so push object to changes
          if (leftObjectKeyNames.length < rightObjectKeys.length) {
            changes.push({ key: k, value: merge(v, right[k]), path });
          } else { // Cycle through object and check if any values differ, if so, set isDiff to true to push parent object to changes
            Object.entries(v).forEach(([objectKey, objectValue]) => {
              if (!isDiff) {
                if (isObject(objectValue) && isObject(right[k][objectKey])) { // If item is object, start a nestedObjectDiff process where it can break out when a single difference is found
                  isDiff = nestedObjectDiff(objectValue, right[k][objectKey]);
                } else if (!isDiff && right[k][objectKey] !== objectValue) { // If the item is just a value make a comparison, if the difference mark as so
                  isDiff = true;
                }

                if (isDiff) { changes.push({ key: k, value: merge(v, right[k]), path }); } // Add object to change list if there were differences (merge left + right, where right overwrites)
              }
            });
          }
        }
      } else if (right[k] !== v) { // If it's just a value make comparison, if there is a difference push new value
        changes.push({ key: k, value: right[k], path });
      }
    });

    if (Object.entries(left).length < Object.entries(right).length) {  // If the number of keys in the object differ, then the object has changed
      const leftKeys = Object.entries(left).map(([lk, _lv]) => lk);
      const missingKeys = Object.entries(right).filter(([k, _v]) => !leftKeys.includes(k)); // Filter out keys that exist on the left to find missing
      missingKeys.forEach(([mk, _mv]) => changes.push({ key: mk, value: right[mk], path: [] })); // Add missing keys to change list
    }
  };

  // Finding the difference when inside an array/object
  const nestedObjectDiff = (left = {}, right = {}) => {
    let isDiff = false;
    
    if (Array.isArray(left) && Array.isArray(right)) {
      if (left.length !== right.length) { return true; } // If there are a different number of array items it means there are differences and parent array is added to changes
      left.forEach((arrayValue, index) => { // If amount of items are the same, loop through and check each one for differences
        if (isObject(arrayValue) && isObject(right[index])) { // If comparison is between object/array then call another nestedObjectDiff to further compare
          isDiff = nestedObjectDiff(arrayValue, right[index]);
        } else if (right[index] !== arrayValue) { isDiff = true; } // If left and right are different return that array is different to parent
      });
    } else {
      const leftObjectKeys = Object.entries(left);
      const rightObjectKeys = Object.entries(right);

      // If there are a different amount of keys then the object is different and we should add the parent array to changes
      if (leftObjectKeys.length < rightObjectKeys.length) { return true; }

      // If the amount of keys are the same, loop through to see if there are any differences
      Object.entries(left).forEach(([k, v]) => {
        if (Array.isArray(v) && Array.isArray(right[k])) { // If object value is an array loop through each item and check for diff
          if (v.length !== right[k.length]) {  // If there are a different number of items in the arrays then they are different
            isDiff = true;
          } else { // Go through array and compare items
            v.forEach((arrayValue, index) => {
              if (isObject(arrayValue) && isObject(right[k][index])) { // If array value is object/array do an nestedObjectDiff on it and if there is a difference end cycle by returning true
                isDiff = nestedObjectDiff(arrayValue, right[k][index]);
              } else if (arrayValue !== right[index]) { // Otherwise compare values between left/right
                isDiff = true;
              }
            });
          }    
        } else if (isObject(v) && isObject(right[k])) { // If value is an object do nestedObjectDiff to go to next level, all differences will be mapped to changes array
          isDiff = nestedObjectDiff(v, right[k]);
        } else if (right[k] !== v) { isDiff = true; } // If there are object differences or the value is not an object and is different, break cycle and return true
      });
    }
  
    return isDiff;
  };

  // Execute finding differences
  innerObjDiff(l, r);

  // Cycle through array of changes and build the object to send to user
  changes.forEach(({ key, value, path: fullPath }) => {
    const pathString = fullPath.length > 0 ? fullPath.join('.') : '';
    setWith(diffObject, pathString === '' ? key : `${pathString}.${key}`, value);
  });

  return diffObject;
};

